import { useEffect, useState } from 'react'
const smallLeakThreasholdLitresPerHour = 60
const mediumLeakThreasholdLitresPerHour = 120

interface SeekALeakInterface {
  bottomContent?: string
  hideCostLPH?: number
  marginOfError?: number
  costPerKL?: number
  billingPeriodDays?: number
  saveWaterUrl?: string
  handleReset: () => void
}

const Completed = ({
  bottomContent,
  hideCostLPH = 1,
  marginOfError = 5,
  costPerKL = 20,
  billingPeriodDays = 60,
  handleReset,
  saveWaterUrl = '#'
}: SeekALeakInterface) => {
  const [litresLostPerHour, setLitresLostPerHour] = useState<number>(0)
  const [leakSize, setLeakSize] = useState<'small' | 'medium' | 'large'>()

  useEffect(() => {
    const start = window.localStorage.getItem('start-time')
    const startReading = window.localStorage.getItem('start-reading')
    const end = window.localStorage.getItem('end-time')
    const endReading = window.localStorage.getItem('end-reading')
    let litresLost = Number(endReading) - Number(startReading)
    if (litresLost < 0) {
      litresLost = litresLost + 1000 // Add 1000 to the meter reading if it ticks over "000"
    }
    if (litresLost < marginOfError) {
      litresLost = 0
    }
    const durationSeconds = Math.ceil((Number(end) - Number(start)) / 1000)
    const llph = (litresLost / durationSeconds) * 60 * 60
    setLitresLostPerHour(llph)
    const leakyAbstraction: 'small' | 'medium' | 'large' | undefined =
      llph < smallLeakThreasholdLitresPerHour
        ? 'small'
        : llph < mediumLeakThreasholdLitresPerHour
          ? 'medium'
          : 'large'
    setLeakSize(leakyAbstraction)
  }, [])

  if (!litresLostPerHour) {
    return (
      <div className="wc-seek-a-leak-next-actions wc-msg wc-msg--grey">
        <div className="next-action">
          <h4 className="next-action-title">No leak detected</h4>
          <div>
            Good news! We didn't detect a leak! Find more ways to{' '}
            <a href={saveWaterUrl}>save water</a>.
          </div>
        </div>
        <input
          className="wc-seek-a-leak-restart"
          type="submit"
          value="&rsaquo; Restart Test"
          onClick={handleReset}
        />
      </div>
    )
  }

  return (
    <div>
      <form className="wc-seek-a-leak wc-msg wc-msg--grey">
        <section className="results-state">
          <h4 className="wc-leak-results-heading">
            You may have a {leakSize} leak.
          </h4>
          <h6 className="wc-leak-results-description">
            Based on the meter readings you've provided you're losing:
          </h6>
          <div
            className={
              litresLostPerHour > hideCostLPH
                ? 'wc-leak-results-values-container'
                : 'hidden'
            }
          >
            <div className="wc-leak-results-values-kpi">
              <div className="wc-leak-results-values-kpi-value">
                {formatNumber(litresLostPerHour)}
              </div>
              <div className="wc-leak-results-values-kpi-units">
                litres per hour
              </div>
            </div>
            <div className="wc-leak-results-values-kpi">
              <div className="wc-leak-results-values-kpi-value">
                {formatNumber(litresLostPerHour * 24)}
              </div>
              <div className="wc-leak-results-values-kpi-units">
                litres per day
              </div>
            </div>
          </div>
          <h6 className="wc-leak-results-description">
            We estimate you're paying an extra:
          </h6>
          <div className="wc-leak-results-values-container">
            <div className="wc-leak-results-values-kpi">
              <div className="wc-leak-results-values-kpi-value">
                $
                {formatNumber(
                  ((litresLostPerHour * 24 * billingPeriodDays) / 1000) *
                  costPerKL
                )}
              </div>
              <div className="wc-leak-results-values-kpi-units">per bill</div>
            </div>
            <div className="wc-leak-results-values-kpi">
              <div className="wc-leak-results-values-kpi-value">
                $
                {formatNumber(
                  ((litresLostPerHour * 24 * 365) / 1000) * costPerKL
                )}
              </div>
              <div className="wc-leak-results-values-kpi-units">per year</div>
            </div>
          </div>
          <input
            className="wc-seek-a-leak-restart"
            type="reset"
            value="&rsaquo; Restart Test"
            onClick={handleReset}
          />
        </section>
      </form>
      <section className="wc-seek-a-leak-next-actions wc-msg wc-msg--grey">
        <div className="next-action">
          <h4 className="next-action-title">What to do next</h4>
          <div className={leakSize === 'small' ? '' : 'hidden'}>
            <p>
              This leak is wasting precious water and costing you money. To
              locate the source of the leak:
            </p>
            <ul>
              <li className="list-none">Look for dripping taps, showerheads or leaking toilets</li>
              <li className="list-none">
                Check the connections on your washing machine and dishwasher
              </li>
              <li className="list-none">Check for cracks or holes in hoses</li>
              <li className="list-none">
                Look for leaky air conditioning units or hot water systems
              </li>
            </ul>
          </div>
          <div className={leakSize === 'medium' ? '' : 'hidden'}>
            <p>
              Get this fixed as soon as you can. You're wasting precious water,
              it's costing you money and may be causing damage to your home.
            </p>
            <p>A leak this size can mean an underground pipe is leaking:</p>
            <ul>
              <li className="list-none">Look for wet patches and/or sunken paving</li>
              <li className="list-none">Damp patches on walls</li>
              <li className="list-none">Particularly green patches of lawn</li>
              <li className="list-none">Sound of running water</li>
            </ul>
          </div>
          <div className={leakSize === 'large' ? '' : 'hidden'}>
            <p>
              Get this fixed as soon as you can. You're wasting precious water,
              it's costing you money and may be causing damage to your home.
            </p>
          </div>
        </div>
        <div className="next-action">
          <div dangerouslySetInnerHTML={{ __html: bottomContent ?? '' }} />
        </div>
      </section>
    </div>
  )
}

// Adds thousand's separator and rounds to the nearast whole number
function formatNumber(num) {
  const n = Math.round(num)
  return n.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
}

export default Completed
